import type { FlatSurface } from '@repo/api-contracts';
import { type PointerEvent, type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import {
  clientToPhotoPoint,
  type PhotoDimensions,
  type Rect,
  type ResizeHandle,
  resizeRectByHandle,
  surfacePhotoToMm,
} from '@/entities/edit-area';

interface Options {
  svgRef: RefObject<SVGSVGElement | null>;
  photoSize: PhotoDimensions;
  disabled: boolean;
  /** 놓은 사각형(표면 mm)으로 크기를 바꾼다. 거부되면 사유를 돌려주고, 그때는 원래 크기 그대로다. */
  onResize: (componentId: string, rect: Rect) => string | null;
}

/** 끄는 동안의 영역 사각형(표면 mm). 저장 원본에는 놓을 때만 들어간다. */
export interface ResizePreview {
  readonly componentId: string;
  readonly rect: Rect;
}

interface Drag {
  readonly pointerId: number;
  readonly handle: ResizeHandle;
  readonly componentId: string;
  readonly surface: FlatSurface;
  readonly start: Rect;
  /** 마지막으로 계산한 사각형. 사진 점을 표면 mm 로 옮길 수 없으면 바뀌지 않는다. */
  readonly last: Rect;
}

const sameRect = (a: Rect, b: Rect): boolean =>
  a.xMm === b.xMm && a.yMm === b.yMm && a.widthMm === b.widthMm && a.heightMm === b.heightMm;

/**
 * 선택된 평면 영역의 핸들 끌기. 반대쪽 모서리·변을 고정한 채 1mm 로 맞춘 사각형을 미리 보이고(Shift 는 비율 유지),
 * 놓을 때 한 번 크기를 바꾼다. 거부되면 미리보기를 버리고 사유를 보인다.
 */
export const useResizePointer = ({ svgRef, photoSize, disabled, onResize }: Options) => {
  const drag = useRef<Drag | null>(null);
  const [preview, setPreview] = useState<ResizePreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cancel = useCallback((): void => {
    const pointerId = drag.current?.pointerId;
    drag.current = null;
    setPreview(null);
    if (pointerId !== undefined && svgRef.current?.hasPointerCapture(pointerId))
      svgRef.current.releasePointerCapture(pointerId);
  }, [svgRef]);
  useEffect(() => {
    if (disabled) cancel();
  }, [disabled, cancel]);
  /** 거부 사유를 지운다(선택이 바뀌거나 Esc 일 때). */
  const dismiss = useCallback((): void => setError(null), []);

  const start = (
    event: PointerEvent<SVGElement>,
    handle: ResizeHandle,
    componentId: string,
    surface: FlatSurface,
    rect: Rect,
  ): void => {
    if (disabled || event.button !== 0 || svgRef.current === null) return;
    if (photoSize.widthPx <= 0 || photoSize.heightPx <= 0) return;
    event.preventDefault();
    event.stopPropagation();
    drag.current = {
      pointerId: event.pointerId,
      handle,
      componentId,
      surface,
      start: rect,
      last: rect,
    };
    setError(null);
    svgRef.current.setPointerCapture(event.pointerId);
  };
  /** 끄는 포인터 이벤트로 바꾼 사각형. 다른 포인터·svg 없음이면 null, 표면 mm 로 옮길 수 없으면 마지막 사각형. */
  const rectAt = (event: PointerEvent<SVGElement>): Rect | null => {
    const current = drag.current;
    if (current === null || event.pointerId !== current.pointerId || svgRef.current === null)
      return null;
    const point = clientToPhotoPoint(svgRef.current, { x: event.clientX, y: event.clientY });
    const to = point === null ? null : surfacePhotoToMm(current.surface, photoSize, point);
    if (to === null) return current.last;
    return resizeRectByHandle(current.surface.realSize, current.start, current.handle, to, {
      keepRatio: event.shiftKey,
    });
  };
  const move = (event: PointerEvent<SVGElement>): void => {
    const current = drag.current;
    const rect = rectAt(event);
    if (current === null || rect === null) return;
    drag.current = { ...current, last: rect };
    setPreview({ componentId: current.componentId, rect });
  };
  const finish = (event: PointerEvent<SVGElement>): void => {
    const current = drag.current;
    if (current === null || event.pointerId !== current.pointerId) return;
    const rect = rectAt(event) ?? current.last;
    cancel();
    if (disabled || sameRect(rect, current.start)) return;
    setError(onResize(current.componentId, rect));
  };

  return { preview, error, start, move, finish, cancel, dismiss };
};
